import type { z } from "zod/mini"

import {
	ColorContentSchema,
	DateContentSchema,
	FieldContentType,
	NumberContentSchema,
	RangeContentSchema,
	SelectContentSchema,
	TextContentSchema,
	TimestampContentSchema,
} from "../field"
import type {
	ColorContent,
	DateContent,
	NumberContent,
	RangeContent,
	SelectContent,
	TextContent,
	TimestampContent,
} from "../field"
import type { StringLegacy } from "../legacy/field"
import { StringLegacySchema } from "../legacy/field"
import type { LegacyContentCtx, LegacyCodec } from "./legacyContentCtx"

type LegacyFieldContent =
	| TextContent
	| ColorContent
	| DateContent
	| TimestampContent
	| NumberContent
	| RangeContent
	| SelectContent

function createFieldLegacyCodec<TContent extends LegacyFieldContent>(
	ctx: LegacyContentCtx,
	name: string,
	type: TContent["type"],
	schema: z.ZodMiniType<TContent>,
): LegacyCodec<TContent, StringLegacy> {
	return {
		name,
		is(input): input is TContent {
			return schema.safeParse(input).success
		},
		toContent(input) {
			const parsed = StringLegacySchema.safeParse(input)
			if (!parsed.success) {
				return parsed
			}

			return {
				success: true,
				data: {
					__TYPE__: FieldContentType,
					type,
					value: parsed.data,
				} as TContent,
			}
		},
		fromContent(input) {
			return {
				content: input.value,
				types: { [ctx.keyOfType]: type },
				keys: {},
			}
		},
	}
}

export const TextLegacyCodec = (ctx: LegacyContentCtx): LegacyCodec<TextContent, StringLegacy> =>
	createFieldLegacyCodec(ctx, "TextLegacy", "Text", TextContentSchema)

export const ColorLegacyCodec = (ctx: LegacyContentCtx): LegacyCodec<ColorContent, StringLegacy> =>
	createFieldLegacyCodec(ctx, "ColorLegacy", "Color", ColorContentSchema)

export const DateLegacyCodec = (ctx: LegacyContentCtx): LegacyCodec<DateContent, StringLegacy> =>
	createFieldLegacyCodec(ctx, "DateLegacy", "Date", DateContentSchema)

export const TimestampLegacyCodec = (
	ctx: LegacyContentCtx,
): LegacyCodec<TimestampContent, StringLegacy> =>
	createFieldLegacyCodec(ctx, "TimestampLegacy", "Timestamp", TimestampContentSchema)

export const NumberLegacyCodec = (
	ctx: LegacyContentCtx,
): LegacyCodec<NumberContent, StringLegacy> =>
	createFieldLegacyCodec(ctx, "NumberLegacy", "Number", NumberContentSchema)

export const RangeLegacyCodec = (ctx: LegacyContentCtx): LegacyCodec<RangeContent, StringLegacy> =>
	createFieldLegacyCodec(ctx, "RangeLegacy", "Range", RangeContentSchema)

export const SelectLegacyCodec = (
	ctx: LegacyContentCtx,
): LegacyCodec<SelectContent, StringLegacy> =>
	createFieldLegacyCodec(ctx, "SelectLegacy", "Select", SelectContentSchema)
